// LoomRaga · <loomraga-carousel>
// Minimal, dependency-free horizontal carousel. CSS scroll-snap does the layout;
// this only wires prev/next buttons and their disabled states. It initialises
// lazily — listeners attach the first time the carousel scrolls into view — and
// respects prefers-reduced-motion.

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class LoomragaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lr-track]');
    this.prevBtn = this.querySelector('[data-lr-prev]');
    this.nextBtn = this.querySelector('[data-lr-next]');
    if (!this.track) return;

    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this._io.disconnect();
            this.init();
          }
        },
        { rootMargin: '200px' }
      );
      this._io.observe(this);
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this._io?.disconnect();
    this.track?.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onScroll);
  }

  init() {
    if (this._ready) return;
    this._ready = true;
    this._onScroll = this.updateButtons.bind(this);
    this.prevBtn?.addEventListener('click', () => this.scrollByItems(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByItems(1));
    this.track.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
    this.updateButtons();
  }

  get step() {
    const item = this.track.querySelector('[data-lr-item]');
    if (!item) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  scrollByItems(dir) {
    this.track.scrollBy({
      left: dir * this.step,
      behavior: REDUCED.matches ? 'auto' : 'smooth',
    });
  }

  updateButtons() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth - 1;
    this.prevBtn?.toggleAttribute('disabled', scrollLeft <= 0);
    this.nextBtn?.toggleAttribute('disabled', scrollLeft >= max);
  }
}

if (!customElements.get('loomraga-carousel')) {
  customElements.define('loomraga-carousel', LoomragaCarousel);
}
